import { fetchApi } from './api';
import type { ProductCatApiParams } from '@/utils/productCatFilters';

// Interfaces existentes
export interface SupplyKpi {
  estimatedCost: number;
  reorderCount: number;
  reviewProgress: number;
  validationPercentage: number;
  validatedProducts: number;
  totalProductsWithSuggestions: number;
}

export type ReplenishmentNeed = 'urgent' | 'needs_reorder' | 'no_need' | 'aware';

export interface SupplyFilters {
  families: Array<{ id: number; name: string }>;
  subfamilies: Array<{ id: number; name: string }>;
  suppliers: Array<{ id: number; name: string }>;
  lifecycleStages: Array<{ id: number; name: string }>;
}

export interface ReorderProduct {
  id: string;
  code: string;
  name: string;
  family: string;
  lifecycleStage: string;
  stock: number;
  reorderPoint: number;
  leadTime: number;
  monthlyAvgSales: number;
  category: string;
  suggestedOrder: number;
  orderQuantity: number;
  moq: number;
  supplier: string;
}

// Nueva interface para la tabla Supply completa
export interface SupplyTableProduct {
  id: number;
  code: string;
  name: string;
  family: string;
  /** Presente en API supply-table; necesario para filtrar en cliente por familia. */
  family_id?: number | null;
  brand_id?: number | null;
  cat_1?: string | null;
  cat_2?: string | null;
  cat_3?: string | null;
  cat_4?: string | null;
  cat_5?: string | null;
  cat_6?: string | null;
  category?: string;
  lifecycle_stage: {
    name: string;
    order: number;
  };
  abc_category: string;
  stock_actual: number;
  stock_en_transito: number;
  /** Lotes activos con stock > 0 en el almacén (contexto supply-table). */
  active_lot_count?: number;
  /** True si active_lot_count > 1 (stock fragmentado en varias partidas). */
  has_fragmented_stock?: boolean;
  /**
   * Grupo de equivalencia (mismo shape que ReorderPoint.equivalence).
   * Usado en sidebar desde Salud de Inventario (INV-EQV-001).
   */
  equivalence?: {
    principal_product_id: number;
    principal_code: string | null;
    canonical_product_id: number;
    conversion_pending: boolean;
    members: Array<{
      product_id: number;
      code: string;
      name: string;
      is_principal: boolean;
      stock: number;
    }>;
    stock_group_total: number;
  } | null;
  venta_media_diaria: number;
  /** Promedio de totales mensuales (demand_prediction); null/undefined => "—" en sidebar. */
  consumo_mensual_estimado?: number | null;
  /** Semanas ISO futuras usadas en el promedio del estimado. */
  consumo_mensual_estimado_weeks_used?: number;
  /** Horizonte aproximado en meses (weeks / 4.4). */
  consumo_mensual_estimado_months_horizon?: number | null;
  /** Promedio mensual histórico (SalesHistory), misma lógica que Salud de inventario. */
  monthly_avg_sales?: number;
  /** Meses con ventas > 0 usados en el divisor del promedio. */
  monthly_avg_sales_months_used?: number;
  /** Ventana de análisis configurada (meses calendario), default 6. */
  monthly_avg_sales_window_months?: number;
  reorder_point: number;
  /** Safety stock usado en el cálculo (`SuggestedQuantity.safety_stock_calculo`). */
  safety_stock_calculo?: number;
  // Stock de seguridad interno de la política de inventario
  internal_safety_stock: number;
  /** Contexto al abrir el sidebar desde Políticas de inventario (fila ReorderPoint). */
  inventory_policy__reorder_point?: number | null;
  inventory_policy__reorder_point_suggested?: number;
  inventory_policy__safety_stock?: number | null;
  inventory_policy__safety_stock_suggested?: number;
  __fromInventoryPolicies?: boolean;
  cantidad_sugerida_sistema: number;
  /** Cantidad sugerida tras aplicar reglas de negocio (opcional; with_rules=true). */
  cantidad_sugerida_ia_reglas?: number;
  /** Reglas aplicadas a la fila (opcional; with_rules=true). */
  applied_rules?: Array<{ id: number; rule_type: string; scope_type: string; parameters_json?: Record<string, unknown> }>;
  rules_explanation?: string;
  /** Base del sistema (antes de aplicar MP/MOQ) */
  cantidad_sugerida_sistema_base?: number;
  production_scope?: {
    gross: number;
    covered: number;
    net: number;
    net_post_moq: number;
    outside_lt: boolean;
    trace_ids: number[];
    production_order_ids: number[];
    pending?: boolean;
    /** Solo con fuente motor (pegged): fecha de necesidad más temprana del alcance. */
    source?: 'engine';
    need_date?: string | null;
    replenishment_need?: string | null;
    quantity_in_pending_orders?: number;
    sales_orders?: Array<{ sales_order_id: number | null; quantity: number }>;
    weeks?: Array<{ week: string; iso_week: string; quantity: number }>;
  };
  cantidad_sugerida_usuario: number | null;
  suggested_quantity_id: number | null;
  supplier_id: number | null;
  supplier_name: string;
  lead_time: number;
  /** True si el LT de la fila producto–proveedor fue fijado manualmente. */
  lead_time_is_static?: boolean;
  /** Frecuencia de compra del proveedor principal (días). */
  purchase_frequency?: number;
  /** Variante expuesta por algunos endpoints legacy (días). */
  purchase_frequency_days?: number;
  moq: number;
  mp: number;
  last_purchase_price: number;
  /** final_price × cantidad final (tramo o cost fallback). TASK 14. */
  suggested_line_total?: number;
  price_from_tier?: boolean;
  product_supplier_id?: number;
  replenishment_need?: ReplenishmentNeed | null;
  needs_reorder: boolean;
  /** Días de cobertura actual (misma fórmula que Salud de inventario). null = sin ventas históricas. */
  coverage_current_days?: number | null;
  /** Umbral de la organización. null = baja cobertura apagada. */
  organization_ideal_coverage_days?: number | null;
  /** Override del proveedor de la fila (null = hereda org). */
  supplier_ideal_coverage_days?: number | null;
  /** Umbral efectivo: override de proveedor si existe, si no el de la organización. */
  ideal_coverage_days?: number | null;
  /** True si hay umbral, cobertura_actual < umbral y cantidad accionable > 0. Urgente no se modifica. */
  is_low_coverage?: boolean;
  // Peso unitario del producto (usado para calcular importe total: cantidad * peso_unitario * precio)
  peso_unitario?: number;
  // Stock de seguridad asociado al proveedor actualmente seleccionado para el producto
  supplier_safety_stock?: number;
  unit_id: number;
  unit_name: string;
  unit_abbreviation: string;
  supply_type: string;
  /** Presente en API supply-table; necesario para filtrar en cliente por tipo. */
  supply_type_id?: number | null;
  /** supply_type.on_demand (tipo MRP / BOM) */
  on_demand?: boolean;
  /** min(week_bucket) en supply_bom_trace para este producto como componente; una query agregada en el backend */
  bom_need_week_min?: string | null;
  is_validated: boolean;
  available_suppliers: Array<{
    supplier_id: number;
    product_supplier_id?: number;
    supplier_name: string;
    lead_time: number;
    purchase_frequency?: number;
    moq: number;
    mp: number;
    last_purchase_price: number;
    /** Coste base (ProductSupplier.cost) antes de aplicar tramo. */
    base_unit_price?: number;
    /** Tramos Gamma Listino para recálculo al editar cantidad (SUP-ORD-008). */
    price_tiers?: Array<{
      quantity_from: number;
      quantity_to: number | null;
      final_price: number;
    }>;
    suggested_line_total?: number;
    price_from_tier?: boolean;
    is_primary: boolean;
    unit_id: number;
    unit_name: string;
    unit_abbreviation: string;
    // Stock de seguridad específico del proveedor en esta relación
    supplier_safety_stock?: number;
    // Cantidades sugeridas específicas del proveedor
    cantidad_sugerida_base?: number;
    cantidad_sugerida_ajustada?: number;
    reorder_point_calculo?: number;
    /** Safety stock del SuggestedQuantity del proveedor en este almacén. */
    safety_stock_calculo?: number;
    replenishment_need?: ReplenishmentNeed | null;
    // Venta media diaria específica del proveedor
    venta_media_diaria?: number;
    /** SuggestedQuantity del proveedor en este almacén (supply-table). */
    suggested_quantity_id?: number | null;
    cantidad_usuario?: number | null;
    is_validated?: boolean;
    editado?: boolean;
  }>;
}

export interface SupplyTableResponse {
  data: SupplyTableProduct[];
  count: number;
  pagination?: {
    current_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
    next_page: number | null;
    previous_page: number | null;
  };
  filters_applied: {
    supplier_id: string | null;
    family_id: string | null;
    lifecycle_id: string | null;
    supply_type_id: string | null;
    needs_reorder_only: boolean;
    warehouse_id: string | null;
  };
  kpis?: {
    suppliers_needing_reorder: number;
    top_suppliers_needing_reorder: Array<{
      supplier_id: number;
      supplier_name: string;
      count: number;
    }>;
  };
  /** Visibility of optional columns (key = column id, value = show). Missing key = show. Product, code, cantidad sugerida always shown. */
  column_config?: Record<string, boolean>;
  /** Configuración org: mostrar abs(stock) como sugerido en bajo pedido con stock negativo. */
  on_demand_negative_stock_as_suggested?: boolean;
  production_scope?: {
    week: string | null;
    iso_week: string | null;
    so_external_id: string | null;
    enabled?: boolean;
    empty_kind?: 'covered' | 'blank' | string;
    /** 'engine' = cantidades del motor (on-demand-week); 'internal' = neteo Django. */
    source?: 'engine' | 'internal';
    /** Hora de la última corrida real del motor (frescura del dato). */
    traces_written_at?: string | null;
    /** Semanas leídas del motor (SO sin semana = varias). */
    weeks?: string[] | null;
  } | null;
  /** Agregados sobre el dataset completo (misma query que la tabla, orden global). */
  table_metrics?: {
    total_products: number;
    products_needing_reorder: number;
    products_urgent?: number;
    total_suggested_value: number;
  };
  rules_pipeline_summary?: Array<{
    step_type: string;
    rule_id: number | null;
    rule_type: string;
    rule_category?: string;
    label: string;
    applied: boolean;
    message: string;
    delta_units?: number;
    delta_amount?: number;
    delta_pallets?: number;
    delta_bases?: number;
    pallets_after?: number;
    bases_after?: number;
  }>;
}

/** Column visibility config for supply table. Keys match backend SUPPLY_TABLE_OPTIONAL_COLUMN_IDS. */
export type SupplyTableColumnConfig = Record<string, boolean>;

import { type ProductCatFilterField } from '@/utils/productCatFilters';

export type SupplyTableFilters = {
  families: Array<{ id: number; name: string }>;
  lifecycle_stages?: Array<{ id: number; name: string }>;
  suppliers: Array<{ id: number; name: string }>;
  supply_types: Array<{ id: number; name: string; on_demand?: boolean }>;
  products: Array<{ id: number; code: string; name: string }>;
  production_weeks?: Array<{ week: string; iso_week: string; label: string }>;
  so_external_ids?: string[];
  production_scope_enabled?: boolean;
} & Partial<Record<ProductCatFilterField, Array<{ value: string; label: string }>>>;

export const EMPTY_SUPPLY_TABLE_FILTERS: SupplyTableFilters = {
  families: [],
  suppliers: [],
  supply_types: [],
  products: [],
};

/** Última respuesta exitosa de supply-table/filters/ (reutilizada al volver a Supply). */
let supplyTableFiltersMemoryCache: SupplyTableFilters | null = null;

export function getCachedSupplyTableFilters(): SupplyTableFilters | null {
  return supplyTableFiltersMemoryCache;
}

export function clearCachedSupplyTableFilters(): void {
  supplyTableFiltersMemoryCache = null;
}

export interface PendingOrderItem {
  id: number;
  orden: number;
  producto: number;
  producto_code: string;
  /** Código del producto en el cliente/proveedor (`Product.client_code`). */
  producto_client_code?: string | null;
  producto_name: string;
  cantidad: number;
  /** Múltiplo de pedido del proveedor (step de flechas en UI). */
  mp?: number;
  /** Mínimo de orden del proveedor (piso al confirmar cantidad en UI). */
  moq?: number;
  unit: number;
  unit_name: string;
  precio_estimado_unitario: number;
  discount?: number | null;
  // Peso unitario del producto (usado para calcular importe total: cantidad * peso_unitario * precio)
  peso_unitario?: number;
  total_value: number;
  created_at: string;
  updated_at: string;
}

export interface PendingOrder {
  id: number;
  numero_orden: string;
  proveedor: number;
  proveedor_name: string;
  proveedor_code: string;
  usuario_creador: number;
  usuario_creador_name: string;
  fecha: string;
  items_count: number;
  total_value: number;
  pending_to_order: boolean;
  discount?: number | null;
  apply_discount?: boolean | null;
  items: PendingOrderItem[];
  created_at: string;
  updated_at: string;

  // Autocompletado de dueDate desde backend
  lead_time_days_assigned?: number;
  lead_time_days_supplier_candidate?: number;
  lead_time_source?: string;
  suggested_due_date?: string;
  production_week?: string | null;
  so_external_id?: string | null;
  /** ERP orderType / TIPO_PARTE. Empty and STD are the default bucket. */
  product_prod_type?: string | null;
}

export interface PendingOrderCreateResponse {
  success: boolean;
  message: string;
  total_created: number;
  total_updated: number;
  suppliers_processed: number;
}

export interface ValidationResponse {
  success: boolean;
  message: string;
  is_validated?: boolean;
  updated_count?: number;
}

// Tipos para integración con ERP
export interface PurchaseOrderLinePayload {
  cod_prod: string;
  quantity: number;
  warehouse_id: string;
  unit_price: number;
  discount?: number | null;
  /** Fecha de entrega específica de la línea (solo front / ERP si lo soporta) */
  due_date?: string;
}

export interface PurchaseOrderPayload {
  organization_id: number;
  supplier_code: string;
  date: string;
  due_date: string;
  comments: string;
  document_lines: PurchaseOrderLinePayload[];
  /** Incluido cuando organization_id >= 10 (ERP writer 2). Valor de supply_management_pendingorder.numero_orden */
  numero_orden?: string;
  /** Compat org=10 en API nueva */
  order_number?: string;
  /** Compat org=10 en API nueva */
  pending_order_id?: number;
  /** Almacén activo (trazabilidad en proxy / logs) */
  warehouse_id?: number;
}

export interface PurchaseOrderResponse {
  success?: boolean;
  message?: string;
  [key: string]: unknown;
}

export interface ProductDetailMonthlySales {
  month: string;
  month_key: string;
  current_year: number;
  previous_year: number;
}

export interface ProductDetailBomTrace {
  id: number;
  order_ref: string | null;
  line_number?: string | null;
  sales_order_number?: string | null;
  root_product_code: string | null;
  component_product_code: string | null;
  qty_root_planned: number;
  qty_component: number;
  /** Semana necesidad del artículo (componente), desde supply_bom_trace.week_bucket */
  week_bucket: string | null;
  /** Lunes ISO semana producción, derivado de sales_productionorder (fin/inicio planificado) */
  production_week_bucket?: string | null;
  externally_managed: boolean;
  managed_by_system_sq: boolean;
  /** Suma cantidad componente por semana menos lo gestionado por ellos */
  suggested_quantity_week: number;
  suggested_quantity: number | null;
  outside_lt?: boolean;
}

export interface ProductDetailInTransitOrder {
  id: number;
  order_number: string;
  supplier_name: string | null;
  sales_orders: string[];
  allocated_quantity?: number;
  quantity: number;
  received_quantity?: number;
  /** Cantidad pendiente de recibir: quantity - received_quantity */
  pending_quantity?: number;
  unit_price: number;
  expected_date: string | null;
  order_date: string | null;
  is_overdue: boolean;
  days_overdue: number | null;
}

export interface ProductDetailPlannedPurchaseOrderRow {
  id: number;
  order_ref: string;
  root_product_code: string | null;
  qty_component: number;
  week_bucket: string | null;
}

export interface ProductDetailPlannedPurchaseOrderSummary {
  total_products: number;
  rows: ProductDetailPlannedPurchaseOrderRow[];
}

export interface ProductDetailSuggestedQuantityInfo {
  cantidad_base: number;
  cantidad_ajustada: number;
  diferencia: number;
  moq: number;
  mp: number;
}

export interface ProductDetailCustomerOrderRow {
  id: number;
  sales_order_number: string;
  customer?: string | null;
  purchase_assignment_state?: 'ASSOCIATED' | 'PENDING' | 'NOT_PROGRAMMED';
  delivery_date: string | null;
  order_date: string | null;
  production_date: string | null;
  order_type: string;
  planning_status: 'PROGRAMMATO' | 'NON_PROGRAMMATO';
  qty_component: number;
  is_component_row?: boolean;
  /** True when the row includes programmed demand from previous weeks. */
  is_past_programmed?: boolean;
  delivery_adjusted_by_production?: boolean;
  parent_stock_available?: number;
  parent_stock_in_transit?: number;
  parent_required_qty?: number;
  parent_stock_insufficient?: boolean;
}

export interface ProductDetailCustomerDemandWeekRow {
  week_start: string;
  planned_qty: number;
  non_planned_qty: number;
  total_qty: number;
  /** True when week is before current week and has programmed qty > 0. */
  is_past_week_programmed?: boolean;
}

export interface ProductDetailCustomerCoverageUntilArrival {
  arrival_date: string;
  lead_time_days: number;
  purchase_frequency_days?: number;
  stock_actual: number;
  stock_in_transit: number;
  consumption_required_until_arrival: number;
  net_available_at_arrival: number;
  forecast_period_qty: number;
  forecast_period_end?: string;
}

export interface ProductDetailCustomerParentNettingRow {
  parent_product_id: number;
  parent_code: string | null;
  parent_name: string | null;
  stock_available: number;
  stock_in_transit: number;
  required_qty: number;
  required_breakdown?: {
    before_lt: {
      planned_qty: number;
      non_planned_qty: number;
    };
    after_lt: {
      planned_qty: number;
      non_planned_qty: number;
    };
  };
  shortage_qty: number;
  has_shortage: boolean;
}

export interface ProductDetailCustomerOrdersSummary {
  from_week: string;
  arrival_week: string;
  lead_time_days: number;
  lead_time_window_end: string;
  source_mode?: 'DIRECT' | 'COMPONENT';
  parent_stock_total?: number;
  demand_by_week?: ProductDetailCustomerDemandWeekRow[];
  coverage_until_arrival?: ProductDetailCustomerCoverageUntilArrival;
  parent_netting?: {
    total_parents: number;
    rows: ProductDetailCustomerParentNettingRow[];
  };
  totals: {
    planned_qty: number;
    non_planned_qty: number;
  };
  leadtime_window_totals: {
    planned_qty: number;
    non_planned_qty: number;
  };
  rows: ProductDetailCustomerOrderRow[];
}

/** Stock / tránsito / lead time (proveedor primario) alineado con la tabla Supply y el almacén del detalle */
export interface BomSupplyMetrics {
  stock_actual: number;
  stock_in_transit: number;
  lead_time_days: number | null;
  primary_supplier_name: string | null;
}

/** Árbol de explosión de materiales (ProductBOM / ProductBOMItem), por unidad del producto raíz */
export interface ProductBomExplosionNode {
  product_id: number;
  code: string;
  name: string;
  product_type?: string | null;
  level: number;
  qty_in_parent: number | null;
  qty_per_root: number;
  unit_abbreviation: string;
  bom_version: number | null;
  bom_id: number | null;
  is_cycle_reference: boolean;
  max_depth_reached: boolean;
  children: ProductBomExplosionNode[];
  supply_metrics?: BomSupplyMetrics;
}

/** Un eslabón hacia arriba: ensamblaje (padre) que consume al hijo en la BOM */
export interface BomParentLink {
  parent_product_id: number;
  parent_code: string;
  parent_name: string;
  parent_product_type?: string | null;
  child_product_id: number;
  child_code: string;
  child_name: string;
  qty_child_per_parent_unit: number;
  unit_abbreviation: string;
  bom_version: number | null;
  is_cycle_reference?: boolean;
  supply_metrics?: BomSupplyMetrics;
}

/** Payload: foco + rutas donde se usa el código + componentes de su BOM propia */
export interface ProductBomExplosionPayload {
  focus: Omit<ProductBomExplosionNode, 'children'>;
  parent_chains: BomParentLink[][];
  downstream_children: ProductBomExplosionNode[];
}

export interface ProductDetailResponse {
  product: {
    id: number;
    code: string;
    name: string;
    product_type?: string;
  };
  monthly_sales: ProductDetailMonthlySales[];
  totals: {
    current_year: number;
    previous_year: number;
    growth_percentage: number;
  };
  /** Solo presente cuando el producto es tipo on_demand */
  is_on_demand?: boolean;
  bom_traces?: ProductDetailBomTrace[];
  /** Líneas de compra en tránsito (siempre presente; puede ser []) */
  in_transit_orders?: ProductDetailInTransitOrder[];
  /** Cantidad total en tránsito sin asignar a sales orders */
  in_transit_unassigned_total?: number;
  /** Cantidad en tránsito con SO vinculada pero parcialmente no asignada */
  in_transit_partially_unassigned_total?: number;
  /** Resumen de purchase orders planificadas previas a la semana actual */
  planned_purchase_orders_summary?: ProductDetailPlannedPurchaseOrderSummary;
  /** Info de cantidad sugerida con ajustes MP/MOQ (on_demand) */
  suggested_quantity_info?: ProductDetailSuggestedQuantityInfo;
  /** Resumen de pedidos cliente para componentes no on_demand */
  customer_orders_summary?: ProductDetailCustomerOrdersSummary;
  /** Explosión y rutas ascendentes (omitido si product_type es FINISHED_DIST) */
  bom_explosion?: ProductBomExplosionPayload;
  /** Lotes físicos en almacén (TASK 12). */
  stock_lots?: ProductDetailStockLot[];
  has_fragmented_stock?: boolean;
}

export interface ProductDetailStockLot {
  id: number;
  lot_number: string;
  batch_code?: string | null;
  stock_quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  status: string;
  quality_status?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  best_before_date?: string | null;
  received_date?: string | null;
  is_blocked: boolean;
  block_reason?: string | null;
  supplier_name?: string | null;
  /** Sigla para mostrar (uds, kg, …). */
  unit_abbreviation: string;
  /** Nombre completo para tooltip (Unidades, Kilogramo, …). */
  unit_name?: string;
  is_expiring_soon: boolean;
  is_expired: boolean;
}

export interface WeeklySupplierOrder {
  supplier_id: number;
  supplier_name: string;
  last_order_date: string | null;
}

export interface SupplierWithoutRecentOrder {
  supplier_id: number;
  supplier_name: string;
  purchase_frequency_days: number;
  last_order_date: string | null;
  next_management_date: string | null;
  service_day: number | null;
  service_days?: number[];
  total_products: number;
  urgent_products: number;
  needs_reorder_products: number;
  total_amount: number;
}

/** Tarjeta de bandeja en modo familia/categoría (SUP-WRK-004). */
export interface WorkTrayProductGroupItem {
  entity_type: 'family' | 'cat_1' | 'cat_2' | 'cat_3' | 'cat_4' | 'cat_5' | 'cat_6' | string;
  entity_id: number | null;
  entity_key: string;
  entity_name: string;
  last_order_date: string | null;
  total_products: number;
  urgent_products: number;
  needs_reorder_products: number;
  total_amount: number;
}

export type WorkTrayMode = 'supplier' | 'product_group';
export type WorkTrayGroupBy = 'family' | 'cat_1' | 'cat_2' | 'cat_3' | 'cat_4' | 'cat_5' | 'cat_6';

export interface SuppliersByFrequency {
  frequency_days: number | null; // null para grupo de urgentes dentro del rango
  is_urgent_within_range?: boolean; //Flag para identificar grupo especial
  column_key?: string | null;
  column_label?: string | null;
  suppliers: SupplierWithoutRecentOrder[];
  items?: WorkTrayProductGroupItem[];
}

export interface WorkTrayResponse {
  warehouse?: { id: number; code?: string; name?: string };
  work_tray_mode: WorkTrayMode;
  group_by?: WorkTrayGroupBy | string | null;
  results: SuppliersByFrequency[];
}

export interface ProductionWeekTrayItem {
  week: string;
  iso_week: string;
  label: string;
  week_end: string;
  product_count: number;
  urgent_products: number;
  needs_reorder_products: number;
  total_amount: number;
  pending: boolean;
}

export interface ProductionWeekTrayColumn {
  column_key: 'in_lt' | 'outside_lt' | string;
  weeks: ProductionWeekTrayItem[];
}

export interface ProductionWeekTrayResponse {
  warehouse?: { id: number; code?: string; name?: string };
  work_tray_mode: 'production_week';
  group_by?: null;
  results: ProductionWeekTrayColumn[];
  /** Lunes ISO con OP PLANNED, aunque el neto esté cubierto. */
  scheduled_weeks?: string[];
  /** Lunes ISO con demanda en ventana ya cubierta (chip verde). No incluye fuera de LT. */
  covered_weeks?: string[];
  /** Fuente motor: rango pedido y hora de la última corrida real. */
  source?: 'engine';
  week_from?: string;
  week_to?: string;
  traces_written_at?: string | null;
}

export interface ProductionWeekPurchaseLine {
  source: 'ordered' | 'received' | 'pending_send' | string;
  order_number: string;
  order_date: string | null;
  expected_date: string | null;
  qty: number;
  qty_received: number;
  supplier_name: string;
}

export interface ProductionWeekPurchaseRow {
  product_id: number;
  code: string;
  name: string;
  qty_ordered: number;
  qty_received: number;
  qty_in_transit: number;
  qty_pending_send: number;
  /** Trazas de L marcadas a mano como gestionadas (SUP-LOT-009); se pueden reabrir. */
  qty_marked_managed?: number;
  last_order_date: string | null;
  sales_orders: Array<{ id: number | null; external_id: string }>;
  purchases: ProductionWeekPurchaseLine[];
}

export interface ProductionWeekPurchasesResponse {
  warehouse?: { id: number; code?: string; name?: string };
  production_week: string | null;
  iso_week: string | null;
  so_external_id?: string | null;
  results: ProductionWeekPurchaseRow[];
}

export interface SupplyOrganizationSettings {
  on_demand_negative_stock_as_suggested: boolean;
  work_tray_mode: WorkTrayMode;
  production_scope_enabled?: boolean;
  on_demand_stock_not_committed?: boolean;
  on_demand_stock_commit_product_ids?: number[];
  delivery_date_snap_to_week_monday?: boolean;
  /** Umbral en días (se guarda aunque la función esté apagada). */
  ideal_coverage_days?: number | null;
  /** Interruptor: false = sin chip ni filas amarillas. */
  low_coverage_enabled?: boolean;
}

/** Opciones para el pipeline de recálculo de SuggestedQuantity (servicio externo). */
export interface SuggestedQuantityCommandOptions {
  dry_run?: boolean;
  force?: boolean;
  hours_threshold?: number;
  dlt_hours_threshold?: number;
  cleanup_obsolete?: boolean;
  skip_bom?: boolean;
  only_dlt?: boolean;
  only_suggested?: boolean;
  only_bom?: boolean;
}

/** Respuesta del pipeline de recálculo. */
export interface SuggestedQuantityCommandResult {
  policies?: Record<string, unknown>;
  dlt?: Record<string, unknown>;
  suggested_quantities?: Record<string, unknown>;
  supplement_so?: Record<string, unknown>;
  bom?: Record<string, unknown>;
  mrp_off_count?: number;
  [key: string]: unknown;
}

export interface SuggestedQuantityItem {
  product_id: number;
  product_code?: string;
  product_name?: string;
  warehouse_id?: number;
  product_supplier_id?: number;
  cantidad_sugerida: number;
  stock_actual?: number;
  stock_en_transito?: number;
  notas_calculo?: string;
}

export interface BomTraceExternalManagedBulkResult {
  success: boolean;
  updated_count: number;
  externally_managed: boolean;
  command_result?: SuggestedQuantityCommandResult;
  suggested_quantities?: SuggestedQuantityItem[];
}

/** Total de tarjetas en la respuesta de bandeja (proveedores o familias/cats). */
export function countSuppliersOnWorkTray(groups: SuppliersByFrequency[]): number {
  return groups.reduce(
    (acc, g) => acc + (g.items?.length ? g.items.length : (g.suppliers?.length ?? 0)),
    0,
  );
}

/**
 * Suma de `total_amount` de todas las tarjetas visibles en bandeja
 * (proveedores o familias/cats). Alineado a SUP-ORD KPI valor estimado sin filtros.
 */
export function sumWorkTrayTotalAmount(groups: SuppliersByFrequency[]): number {
  let total = 0;
  for (const group of groups) {
    if (group.items && group.items.length > 0) {
      for (const item of group.items) {
        total += Number(item.total_amount) || 0;
      }
      continue;
    }
    for (const supplier of group.suppliers || []) {
      total += Number(supplier.total_amount) || 0;
    }
  }
  return Math.round(total);
}

class SupplyService {
  // URL anterior en Azure:
  // https://erpwriterstockerai-f3e0bad9f4b9fscw.spaincentral-01.azurewebsites.net

  // Métodos existentes para compatibilidad
  async getKpiData(filters?: Record<string, string[]>): Promise<SupplyKpi> {
    const params: Record<string, string> = {};
    if (filters) {
      params.filters = JSON.stringify(filters);
    }
    return fetchApi<SupplyKpi>('supply_management/kpi/', { params });
  }

  async getFilters(): Promise<SupplyFilters> {
    return fetchApi<SupplyFilters>('supply_management/filters/');
  }

  async getReorderProducts(filters?: Record<string, string[]>): Promise<ReorderProduct[]> {
    const params: Record<string, string> = {};
    if (filters) {
      params.filters = JSON.stringify(filters);
    }
    return fetchApi<ReorderProduct[]>('supply_management/reorder-products/', { params });
  }

  // Nuevos métodos para la tabla Supply completa
  async getSupplyTableData(params?: {
    supplier_id?: number | number[];
    family_id?: number | number[];
    category_id?: number | number[];
    lifecycle_id?: number | number[];
    supply_type_id?: number | number[];
    product_id?: number | number[];
    needs_reorder?: boolean;
    suggested_greater_zero?: boolean;
    warehouse_id?: number;
    page?: number;
    page_size?: number;
    metrics_only?: boolean;
    with_rules?: boolean;
    apply_moq_mp?: boolean;
    /**
     * Si true, pide el dataset Redis completo en una sola respuesta (HIT)
     * o 409 cache_miss (MISS). No combinar con force_refresh en la misma
     * request: el bump fuerza MISS. Ver SUP-CACHE-001 / fetchAllSupplyTablePages.
     */
    catalog_dump?: boolean;
    /**
     * Si true, el backend invalida el cache server-side del dataset
     * ordenado antes de servir la request. Usar solo cuando el usuario
     * pulsa explicitamente "Actualizar": procesos externos pueden haber
     * reescrito SuggestedQuantity sin disparar el bump natural.
     */
    force_refresh?: boolean;
    production_week?: string;
    so_external_id?: string;
  } & ProductCatApiParams, request?: { signal?: AbortSignal }): Promise<SupplyTableResponse> {
    const queryParams: Record<string, string | number | boolean | string[]> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            // Para arrays, convertir a strings separados por comas
            queryParams[key] = value.map(v => v.toString()).join(',');
          } else if (typeof value === 'boolean') {
            // Asegurar que los booleanos se envíen como booleanos, no como strings
            queryParams[key] = value;
          } else {
            queryParams[key] = value;
          }
        }
      });
    }

    // Debug: verificar parámetros antes de enviar
   

    const response = await fetchApi<SupplyTableResponse>('supply_management/supply-table/', {
      params: queryParams,
      signal: request?.signal,
    });

    return response;
  }

  async getWeeklySupplierOrders(warehouseId: number, urgent_only?: boolean): Promise<WeeklySupplierOrder[]> {
    const params: Record<string, string | number> = { 
      warehouse_id: warehouseId,
      urgent_only: urgent_only === true ? 'true' : 'false'
    };
    const response = await fetchApi<{ results: WeeklySupplierOrder[] }>('supply_management/weekly-supplier-orders/', {
      params
    });
    return response.results || [];
  }

  async getSuppliersWithoutRecentOrders(
    warehouseId: number,
    urgent_only?: boolean,
    apply_moq_mp?: boolean,
    supplier_id?: number,
    options?: { group_by?: WorkTrayGroupBy | string },
  ): Promise<SuppliersByFrequency[]> {
    const response = await this.getWorkTray(warehouseId, urgent_only, apply_moq_mp, supplier_id, options);
    return response.results || [];
  }

  async getWorkTray(
    warehouseId: number,
    urgent_only?: boolean,
    apply_moq_mp?: boolean,
    supplier_id?: number,
    options?: { group_by?: WorkTrayGroupBy | string },
  ): Promise<WorkTrayResponse> {
    const params: Record<string, string | number> = {
      warehouse_id: warehouseId,
      urgent_only: urgent_only === true ? 'true' : 'false',
      apply_moq_mp: apply_moq_mp === false ? 'false' : 'true',
    };
    if (typeof supplier_id === 'number' && Number.isFinite(supplier_id)) {
      params.supplier_id = supplier_id;
    }
    if (options?.group_by) {
      params.group_by = options.group_by;
    }
    return fetchApi<WorkTrayResponse>('supply_management/suppliers-without-recent-orders/', {
      params,
    });
  }

  async getProductionWeekTray(
    warehouseId: number,
    urgent_only?: boolean,
    apply_moq_mp?: boolean,
    /** Ventana visible de la tira (lunes ISO). Con fuente motor acota las llamadas. */
    range?: { week_from: string; week_to: string },
  ): Promise<ProductionWeekTrayResponse> {
    const params: Record<string, string | number> = {
      warehouse_id: warehouseId,
      urgent_only: urgent_only === true ? 'true' : 'false',
      apply_moq_mp: apply_moq_mp === false ? 'false' : 'true',
      ...(range ? { week_from: range.week_from, week_to: range.week_to } : {}),
    };
    return fetchApi<ProductionWeekTrayResponse>('supply_management/production-week-tray/', {
      params,
    });
  }

  async getProductionWeekPurchases(request: {
    warehouseId: number;
    productionWeek?: string;
    soExternalId?: string;
  }): Promise<ProductionWeekPurchasesResponse> {
    const params: Record<string, string | number> = {
      warehouse_id: request.warehouseId,
    };
    if (request.productionWeek) {
      params.production_week = request.productionWeek;
    }
    if (request.soExternalId) {
      params.so_external_id = request.soExternalId;
    }
    return fetchApi<ProductionWeekPurchasesResponse>('supply_management/production-week-purchases/', {
      params,
    });
  }

  /** SUP-LOT-009: marca (o reabre con managed=false) las filas on_demand de L. */
  async markProductionScopeManaged(request: {
    productIds: number[];
    productionWeek?: string;
    soExternalId?: string;
    managed?: boolean;
  }): Promise<{ updated_count: number; managed: boolean; product_ids: number[] }> {
    return fetchApi('supply_management/production-scope-mark-managed/', {
      method: 'POST',
      body: {
        product_ids: request.productIds,
        production_week: request.productionWeek || undefined,
        so_external_id: request.soExternalId || undefined,
        managed: request.managed !== false,
      },
    });
  }

  async getSupplyTableFilters(request?: {
    signal?: AbortSignal;
    production_week?: string;
    catalog?: 'so';
  }): Promise<SupplyTableFilters> {
    const params: Record<string, string> = {};
    if (request?.production_week) {
      params.production_week = request.production_week;
    }
    if (request?.catalog) {
      params.catalog = request.catalog;
    }
    const data = await fetchApi<SupplyTableFilters>('supply_management/supply-table/filters/', {
      params,
      signal: request?.signal,
    });
    if (!request?.production_week && !request?.catalog) {
      supplyTableFiltersMemoryCache = data;
    }
    return data;
  }

  async getSupplyTableColumnConfig(): Promise<{ column_config: SupplyTableColumnConfig }> {
    return fetchApi<{ column_config: SupplyTableColumnConfig }>('supply_management/column-config/');
  }

  async updateSupplyTableColumnConfig(column_visibility: SupplyTableColumnConfig): Promise<{ column_config: SupplyTableColumnConfig }> {
    return fetchApi<{ column_config: SupplyTableColumnConfig }>('supply_management/column-config/', {
      method: 'PATCH',
      body: { column_visibility }
    });
  }

  async getOrganizationSettings(): Promise<SupplyOrganizationSettings> {
    return fetchApi<SupplyOrganizationSettings>('supply_management/settings/');
  }

  async updateOrganizationSettings(payload: Partial<SupplyOrganizationSettings>): Promise<SupplyOrganizationSettings> {
    return fetchApi<SupplyOrganizationSettings>('supply_management/settings/', {
      method: 'PATCH',
      body: payload,
    });
  }

  async updateUserQuantity(productId: number, cantidadUsuario: number | null, warehouseId?: number | null, supplierId?: number | null): Promise<{
    success: boolean;
    suggested_quantity_id: number;
    cantidad_sugerida_sistema: number;
    cantidad_sugerida_usuario: number | null;
  }> {
    const body: any = {
      product_id: productId,
      cantidad_usuario: cantidadUsuario
    };
    // Agregar warehouse_id si se proporciona y no es null
    if (warehouseId !== undefined && warehouseId !== null) {
      body.warehouse_id = warehouseId;
    }
    
    // Agregar supplier_id si se proporciona (desde la orden)
    if (supplierId !== undefined && supplierId !== null) {
      body.supplier_id = supplierId;
    }
    
    return fetchApi('supply_management/supply-table/', {
      method: 'POST',
      body
    });
  }

  // Método auxiliar para calcular cantidad ajustada según MOQ y MP
  calculateAdjustedQuantity(desiredQuantity: number, moq: number, mp: number): number {
    if (desiredQuantity <= 0) return 0;
    
    // Si MP es 1 o no existe, no hay restricción de múltiplos
    if (mp <= 1) {
      return Math.max(desiredQuantity, moq);
    }
    
    // Ajustar al múltiplo más cercano (arriba o abajo)
    const adjustedQuantity = Math.round(desiredQuantity / mp) * mp;
    
    // Asegurar que cumple con MOQ
    if (adjustedQuantity < moq) {
      // Si está por debajo del MOQ, ajustar MOQ al siguiente múltiplo válido
      return Math.ceil(moq / mp) * mp;
    }
    
    return adjustedQuantity;
  }

  // Método para obtener KPIs de la tabla Supply
  async getSupplyTableKpis(filters?: {
    supplier_id?: number | number[];
    family_id?: number | number[];
    category_id?: number | number[];
    cat_1?: string | string[];
    cat_2?: string | string[];
    cat_3?: string | string[];
    cat_4?: string | string[];
    cat_5?: string | string[];
    cat_6?: string | string[];
    lifecycle_id?: number | number[];
  }): Promise<{
    totalProducts: number;
    productsNeedingReorder: number;
    totalSuggestedValue: number;
    averageLeadTime: number;
  }> {
    const data = await this.getSupplyTableData(filters);
    
    const totalProducts = data.count;
    const productsNeedingReorder = data.data.filter(p =>
      p.replenishment_need != null
        ? p.replenishment_need === 'urgent' || p.replenishment_need === 'needs_reorder'
        : p.needs_reorder
    ).length;
    const totalSuggestedValue = data.data.reduce((sum, p) => {
      if (p.suggested_line_total != null) {
        return sum + p.suggested_line_total;
      }
      const finalQuantity = p.cantidad_sugerida_usuario || p.cantidad_sugerida_sistema;
      return sum + (finalQuantity * p.last_purchase_price);
    }, 0);
    const averageLeadTime = data.data.length > 0 
      ? data.data.reduce((sum, p) => sum + p.lead_time, 0) / data.data.length 
      : 0;
    return {
      totalProducts,
      productsNeedingReorder,
      totalSuggestedValue: Math.round(totalSuggestedValue),
      averageLeadTime: Math.round(averageLeadTime * 10) / 10
    };
  }

  async getSupplyAlertsCount(): Promise<number> {
    try {
      // const response = await fetchApi<{ count: number }>('supply/alerts-count/');
      // return response.count;
      return 0;
    } catch (error) {
      console.error('[SupplyService] Error fetching supply alerts count:', error);
      return 0;
    }
  }

  // Métodos para validación de SuggestedQuantity
  async validateSuggestion(suggestionId: number, isValidated: boolean = true): Promise<ValidationResponse> {
    
    const response = await fetchApi<ValidationResponse>('supply_management/validate-suggestion/', {
      method: 'POST',
      body: {
        suggestion_id: suggestionId,
        is_validated: isValidated
      }
    });
    
    return response;
  }

  async bulkValidateSuggestions(suggestionIds: number[], isValidated: boolean = true): Promise<ValidationResponse> {
    return fetchApi<ValidationResponse>('supply_management/bulk-validate-suggestions/', {
      method: 'POST',
      body: {
        suggestion_ids: suggestionIds,
        is_validated: isValidated
      }
    });
  }

  // Métodos para reactivar sugerencias
  async reactivateSuggestion(suggestionId: number): Promise<{ success: boolean; message: string }> {
    return fetchApi<{ success: boolean; message: string }>(`supply_management/suggested-quantities/${suggestionId}/reactivate/`, {
      method: 'POST'
    });
  }

  async reactivateAllInactiveSuggestions(): Promise<{ success: boolean; message: string; statistics: any }> {
    return fetchApi<{ success: boolean; message: string; statistics: any }>('supply_management/suggested-quantities/reactivate_all_inactive/', {
      method: 'POST'
    });
  }

  // Métodos para PendingOrder
  async getPendingOrders(pendingOnly: boolean = true, warehouseId?: number | null): Promise<PendingOrder[]> {
    const warehouseParam = typeof warehouseId === 'number' ? `&warehouse_id=${warehouseId}` : '';
    const cacheBuster = `&_ts=${Date.now()}`;
    const result = await fetchApi<any>(
      `supply_management/pending-orders/?pending_only=${pendingOnly}${warehouseParam}${cacheBuster}`
    );
    
    // Extraer el array de datos si está envuelto en una respuesta paginada
    let orders: PendingOrder[];
    if (result && typeof result === 'object') {
      if (Array.isArray(result)) {
        // Si es directamente un array
        orders = result;
      } else if (result.results && Array.isArray(result.results)) {
        // Si está en formato de respuesta paginada
        orders = result.results;
      } else if (result.data && Array.isArray(result.data)) {
        // Si está en formato de respuesta con data
        orders = result.data;
      } else {
        // Si no es ninguno de los anteriores, intentar usar el objeto completo
        console.warn('⚠️ SupplyService getPendingOrders: Formato de respuesta inesperado, usando objeto completo');
        orders = [result];
      }
    } else {
      orders = [];
    }
    
    return orders;
  }

  async getPendingOrdersBySupplier(supplierId?: number): Promise<PendingOrder[]> {
    const params = supplierId ? { supplier_id: supplierId } : undefined;
    return fetchApi<PendingOrder[]>(`supply_management/pending-orders/by_supplier/`, { params });
  }

  async createPendingOrdersFromValidatedSuggestions(
    selectedSuppliers?: Record<string, number>, 
    filteredProductIds?: number[],
    validatedProducts?: Array<{
      id: number;
      quantity: number;
      suggested: number;
      is_user_edited?: boolean;
    }>,
    applyMoqMp: boolean = true,
    productionScope?: { production_week?: string; so_external_id?: string; warehouse_id?: number },
  ): Promise<PendingOrderCreateResponse> {
    const response = await fetchApi<PendingOrderCreateResponse>('supply_management/pending-orders/create_from_validated_suggestions/', {
      method: 'POST',
      body: {
        selected_suppliers: selectedSuppliers || {},
        filtered_product_ids: filteredProductIds || [],
        validated_products: validatedProducts || [],
        apply_moq_mp: applyMoqMp,
        // SO sin semana (motor): el backend reparte por semana leyendo este almacén.
        ...(productionScope?.warehouse_id ? { warehouse_id: productionScope.warehouse_id } : {}),
        ...(productionScope?.production_week
          ? { production_week: productionScope.production_week }
          : {}),
        ...(productionScope?.so_external_id
          ? { so_external_id: productionScope.so_external_id }
          : {}),
      }
    });
    
    return response;
  }

  async markPendingOrderAsOrdered(orderId: number): Promise<{ success: boolean; message: string }> {
    return fetchApi<{ success: boolean; message: string }>(`supply_management/pending-orders/${orderId}/mark_as_ordered/`, {
      method: 'POST'
    });
  }

  async createPurchaseOrderInErp(payload: PurchaseOrderPayload): Promise<PurchaseOrderResponse> {
    return fetchApi<PurchaseOrderResponse>('supply_management/erp/purchase-order/', {
      method: 'POST',
      body: payload
    });
  }

  // Métodos para edición y eliminación de órdenes pendientes
  async updatePendingOrderItemQuantity(
    _orderId: number,
    itemId: number,
    newQuantity: number,
    newPrice?: number,
    newDiscount?: number | null
  ): Promise<{
    success: boolean;
    message: string;
    updated_item?: PendingOrderItem;
    new_order_total?: number;
  }> {
    // itemId es directamente el ID del PendingOrderItem
    const body: any = {
      quantity: newQuantity
    };
    
    // Agregar precio si se proporciona
    if (newPrice !== undefined) {
      body.precio_estimado_unitario = newPrice;
    }
    if (newDiscount !== undefined) {
      body.discount = newDiscount;
    }
    
    return fetchApi(`supply_management/pending-order-items/${itemId}/update_quantity/`, {
      method: 'PATCH',
      body
    });
  }

  async updatePendingOrder(orderId: number, data: { discount?: number | null; apply_discount?: boolean | null }): Promise<PendingOrder> {
    return fetchApi<PendingOrder>(`supply_management/pending-orders/${orderId}/`, {
      method: 'PATCH',
      body: data
    });
  }

  async duplicatePendingOrderItem(_orderId: number, itemId: number): Promise<{
    success: boolean;
    message: string;
    duplicated_item?: PendingOrderItem;
    new_order_total?: number;
  }> {
    // itemId es directamente el ID del PendingOrderItem
    return fetchApi(`supply_management/pending-order-items/${itemId}/duplicate/`, {
      method: 'POST'
    });
  }

  async deletePendingOrderItem(_orderId: number, itemId: number): Promise<{
    success: boolean;
    message: string;
    new_order_total?: number;
    remaining_items?: number;
  }> {
        // itemId es directamente el ID del PendingOrderItem
        return fetchApi(`supply_management/pending-order-items/${itemId}/`, {
      method: 'DELETE'
    });
  }

  async deletePendingOrder(orderId: number): Promise<{
    success: boolean;
    message: string;
  }> {
    return fetchApi(`supply_management/pending-orders/${orderId}/`, {
      method: 'DELETE'
    });
  }

  async getPendingOrderDetails(orderId: number): Promise<PendingOrder> {
    return fetchApi<PendingOrder>(`supply_management/pending-orders/${orderId}/`);
  }

  async getPendingOrderItems(orderId: number): Promise<PendingOrderItem[]> {
    return fetchApi<PendingOrderItem[]>(`supply_management/pending-order-items/?orden=${orderId}`);
  }

  async getProductDetail(
    productId: number,
    warehouseId?: number,
    supplierId?: number,
    options?: { production_week?: string; so_external_id?: string },
  ): Promise<ProductDetailResponse> {
    const params: Record<string, string> = {
      product_id: productId.toString()
    };
    if (warehouseId) {
      params.warehouse_id = warehouseId.toString();
    }
    if (supplierId) {
      params.supplier_id = supplierId.toString();
    }
    if (options?.production_week) {
      params.production_week = options.production_week;
    }
    if (options?.so_external_id) {
      params.so_external_id = options.so_external_id;
    }
    return fetchApi<ProductDetailResponse>('products/product-detail/', { params });
  }

  async bulkUpdateBomTraceExternalManaged(
    productId: number,
    options: { traceIds?: number[]; salesOrderNumbers?: string[] },
    externallyManaged: boolean
  ): Promise<BomTraceExternalManagedBulkResult> {
    const payload: Record<string, unknown> = {
      product_id: productId,
      externally_managed: externallyManaged,
    };
    if (options.traceIds && options.traceIds.length > 0) {
      payload.trace_ids = options.traceIds;
    }
    if (options.salesOrderNumbers && options.salesOrderNumbers.length > 0) {
      payload.sales_order_numbers = options.salesOrderNumbers;
    }

    return fetchApi<BomTraceExternalManagedBulkResult>(
      'products/product-detail/bom-trace-external-managed-bulk/',
      {
        method: 'POST',
        body: payload,
      }
    );
  }

  /**
   * Llama al pipeline de recálculo de SuggestedQuantity para un producto concreto.
   * El backend proxea la llamada al servicio externo con X-API-Key.
   * Espera la respuesta completa del pipeline antes de resolver.
   */
  async recalculateProduct(
    productId: number,
    options?: SuggestedQuantityCommandOptions
  ): Promise<SuggestedQuantityCommandResult> {
    return fetchApi<SuggestedQuantityCommandResult>(
      `supply_management/recalculate/product/${productId}/`,
      { method: 'POST', body: options ?? {} }
    );
  }

  /**
   * Llama al pipeline de recálculo de SuggestedQuantity para todos los productos
   * de un proveedor. El backend proxea la llamada al servicio externo con X-API-Key.
   * Espera la respuesta completa del pipeline antes de resolver.
   */
  async recalculateSupplier(
    supplierId: number,
    options?: SuggestedQuantityCommandOptions
  ): Promise<SuggestedQuantityCommandResult> {
    return fetchApi<SuggestedQuantityCommandResult>(
      `supply_management/recalculate/supplier/${supplierId}/`,
      { method: 'POST', body: options ?? {} }
    );
  }

  /**
   * Recálculo org-wide (p. ej. tras publicar plan de demanda).
   * Proxy hacia replenishment o suggested-quantity legacy según organización.
   */
  async recalculateOrganization(
    options?: SuggestedQuantityCommandOptions
  ): Promise<SuggestedQuantityCommandResult> {
    return fetchApi<SuggestedQuantityCommandResult>(
      'supply_management/recalculate/organization/',
      { method: 'POST', body: options ?? { force: true } }
    );
  }

}

export const supplyService = new SupplyService();